// frontend/src/modules/reports/pages/financial/PayrollCostPage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Run|month toggle; gross/tax/net trend
// + a bucket-level table, both per currency.

import * as React from 'react';
import { Wallet, Receipt, PiggyBank } from 'lucide-react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { RevenueBars } from '../../../../design-system/charts';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney } from '../../../../design-system/format';
import { reportsApi, downloadReportCsv } from '../../api';
import type { PayrollCostReport, PayrollGroupBy } from '../../types';
import { useReportFetch } from './useReportFetch';
import { groupByCurrency } from './currencyGroup';

const GROUP_BYS: PayrollGroupBy[] = ['run', 'month'];

export function PayrollCostPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [groupBy, setGroupBy] = React.useState<PayrollGroupBy>('month');

  const fetcher = range ? () => reportsApi.payrollCost({ from: range.from, to: range.to, groupBy }) : null;
  const result = useReportFetch<PayrollCostReport>(fetcher, [range?.from, range?.to, groupBy]);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];
  const sections = groupByCurrency(rows);

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="Payroll cost"
      description="Gross, tax, and net payroll cost by run or by month."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
          {GROUP_BYS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={[
                'px-3.5 h-8 rounded-full text-[12px] font-semibold capitalize transition-colors',
                groupBy === g ? 'bg-saffron text-zinc-950' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              By {g}
            </button>
          ))}
        </div>
      }
      csv={range ? {
        filename: `payroll-cost-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'payroll-cost',
          { from: range.from, to: range.to, groupBy },
          `payroll-cost-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <div className="space-y-6">
          {sections.map(([currency, currencyRows]) => {
            const gross = currencyRows.reduce((s, r) => s + r.gross_additions, 0);
            const tax = currencyRows.reduce((s, r) => s + r.tax_total, 0);
            const net = currencyRows.reduce((s, r) => s + r.net_amount, 0);
            const netSeries = currencyRows.map((r) => r.net_amount);
            return (
              <div key={currency} className="space-y-4">
                <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">{currency}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard label="Gross" value={formatMoney(gross, currency)} icon={<Wallet />} />
                  <StatCard label="Tax" value={formatMoney(tax, currency)} icon={<Receipt />} />
                  <StatCard label="Net" value={formatMoney(net, currency)} icon={<PiggyBank />} />
                </div>
                {netSeries.some((v) => v > 0) && (
                  <div className={[VISUAL.card, 'p-6'].join(' ')}>
                    <div className="text-[12px] font-medium text-muted-foreground mb-3">Net payroll per {groupBy === 'run' ? 'run' : 'month'}</div>
                    <RevenueBars series={netSeries} height={120} />
                  </div>
                )}
                <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable
                    rows={currencyRows}
                    rowKey={(r) => `${r.bucket}-${currency}`}
                    columns={[
                      { key: 'bucket', header: groupBy === 'run' ? 'Run' : 'Month', cell: (r) => r.bucket },
                      { key: 'gross_additions', header: 'Gross additions', cell: (r) => formatMoney(r.gross_additions, currency) },
                      { key: 'gross_deductions', header: 'Gross deductions', cell: (r) => formatMoney(r.gross_deductions, currency) },
                      { key: 'tax_total', header: 'Tax', cell: (r) => formatMoney(r.tax_total, currency) },
                      { key: 'net_amount', header: 'Net', cell: (r) => <span className="font-semibold text-foreground">{formatMoney(r.net_amount, currency)}</span> },
                      // A partially-overlapping run is scaled to the range. Show
                      // it per row — an unexplained scaled total is worse than
                      // no total, and this is exactly what a CFO ties out.
                      {
                        key: 'prorated',
                        header: 'Counted',
                        cell: (r) => (r.prorated
                          ? <span className="text-muted-foreground">{r.proration_factor != null ? `${Math.round(r.proration_factor * 100)}% of run` : 'part of run'}</span>
                          : <span className="text-muted-foreground">full run</span>),
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
          {report?.contains_prorated_runs && (
            <p className="text-[12px] text-muted-foreground">
              Some payroll runs cross the edge of this date range. Those are counted only for the
              portion of their pay period that falls inside it, so the totals match the range you asked
              for rather than the whole run.
            </p>
          )}
        </div>
      )}
    </ReportShell>
  );
}
